import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { getAllGames, getTeamById, getTeamPlayers } from "@/utils/nba";
import TeamBadge from "@/components/base/TeamBadge";
import GameCard from "@/components/base/GameCard";

export default function Team() {
  const { teamId } = useParams();
  const team = getTeamById(teamId);

  useSeo({
    title: team ? `${team.fullName}球队资料与阵容 - NBA直播吧` : "球队资料 - NBA直播吧",
    description: team
      ? `${team.fullName}球队资料页，包含球队徽标、历史战绩（${team.championships} 次总冠军）、本赛季 ${team.wins} 胜 ${team.losses} 负战绩、阵容名单与近期比赛结果。`
      : "NBA 球队资料页。",
    keywords: team ? `${team.fullName},${team.abbreviation}球队,NBA球队阵容,NBA球队战绩,NBA直播吧` : "NBA球队资料",
  });

  if (!team) {
    return (
      <div className="mx-auto flex max-w-page flex-col items-center justify-center px-4 py-24 text-center">
        <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-team-line text-2xl" />
        </span>
        <h1 className="font-heading text-xl font-bold text-foreground-950">未找到该球队</h1>
        <Link to="/teams" className="mt-6 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 cursor-pointer">
          返回球队列表
        </Link>
      </div>
    );
  }

  const roster = getTeamPlayers(team.id);
  const recentGames = getAllGames()
    .filter((game) => game.homeTeamId === team.id || game.awayTeamId === team.id)
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, 4);

  const winRate = team.wins + team.losses > 0 ? Math.round((team.wins / (team.wins + team.losses)) * 100) : 0;

  return (
    <div>
      <div className="w-full" style={{ backgroundColor: team.color }}>
        <div className="mx-auto flex max-w-page flex-col gap-5 px-4 py-10 md:flex-row md:items-center md:px-6">
          <TeamBadge team={team} size="lg" />
          <div className="text-background-50">
            <p className="text-sm opacity-80">{team.city} · {team.division}</p>
            <h1 className="font-heading text-2xl md:text-4xl font-bold">{team.fullName}</h1>
            <div className="mt-3 flex flex-wrap gap-4 text-sm">
              <span><i className="ri-trophy-line mr-1" />{team.championships} 次总冠军</span>
              <span><i className="ri-flag-line mr-1" />建队于 {team.founded} 年</span>
              <span><i className="ri-home-4-line mr-1" />{team.arena}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-page px-4 py-8 md:px-6 md:py-10">
        <nav className="mb-6 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-700 cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/teams" className="hover:text-primary-700 cursor-pointer">球队与球员</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-900 font-medium">{team.fullName}</span>
        </nav>

        <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-4">
          {[
            { label: "本赛季战绩", value: `${team.wins}胜${team.losses}负` },
            { label: "胜率", value: `${winRate}%` },
            { label: "总冠军", value: `${team.championships} 次` },
            { label: "阵容人数", value: roster.length > 0 ? `${roster.length} 人` : "—" },
          ].map((item) => (
            <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 p-4 text-center">
              <p className="font-heading text-xl md:text-2xl font-bold text-primary-700">{item.value}</p>
              <p className="mt-1 text-xs text-foreground-500">{item.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="mb-4 font-heading text-xl font-bold text-foreground-950">球队简介</h2>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <p className="text-sm leading-relaxed text-foreground-700">{team.history}</p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                {team.fullName}隶属于{team.conference === "East" ? "东部" : "西部"}联盟{team.division}，主场设立于{team.arena}。
                球队本赛季目前取得 {team.wins} 胜 {team.losses} 负的战绩。
              </p>
            </div>

            <h2 className="mb-4 mt-8 font-heading text-xl font-bold text-foreground-950">本赛季阵容</h2>
            {roster.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                <div className="grid grid-cols-12 gap-2 border-b border-background-200 bg-background-100 px-4 py-3 text-xs font-medium text-foreground-500">
                  <span className="col-span-5">球员</span>
                  <span className="col-span-2 text-center">得分</span>
                  <span className="col-span-2 text-center">篮板</span>
                  <span className="col-span-3 text-center">助攻</span>
                </div>
                {roster.map((player) => (
                  <Link
                    key={player.id}
                    to={`/player/${player.id}`}
                    className="grid grid-cols-12 items-center gap-2 border-b border-background-200 px-4 py-3 text-sm transition-colors last:border-b-0 hover:bg-primary-50 cursor-pointer"
                  >
                    <div className="col-span-5 flex items-center gap-3 min-w-0">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-background-100 text-[11px] font-bold text-foreground-600">
                        {player.number}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate font-medium text-foreground-950">{player.name}</p>
                        <p className="text-[11px] text-foreground-400">{player.position} · {player.height}</p>
                      </div>
                    </div>
                    <span className="col-span-2 text-center font-medium text-foreground-900">{player.seasonStats.ppg}</span>
                    <span className="col-span-2 text-center text-foreground-700">{player.seasonStats.rpg}</span>
                    <span className="col-span-3 text-center text-foreground-700">{player.seasonStats.apg}</span>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="rounded-lg border border-dashed border-background-300 bg-background-50 py-10 text-center text-sm text-foreground-500">
                该球队为全明星联队，暂无固定阵容名单
              </p>
            )}
          </div>

          <div>
            <h2 className="mb-4 font-heading text-xl font-bold text-foreground-950">近期比赛</h2>
            {recentGames.length > 0 ? (
              <div className="space-y-4">
                {recentGames.map((game) => (
                  <GameCard key={game.id} game={game} />
                ))}
              </div>
            ) : (
              <p className="rounded-lg border border-dashed border-background-300 bg-background-50 py-10 text-center text-sm text-foreground-500">
                暂无近期比赛记录
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}